import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { PersonDetail } from "../api";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { SigninMap } from "../lib/SigninMap";
import { ErrorNote, Loading, Page } from "./common";

type Tab = "overview" | "listings" | "realtyid" | "security" | "billing";

function resultTone(result: string) {
  if (result.startsWith("ok")) return { bg: "#e8f4ee", fg: "#1f8a5b" };
  if (result.includes("blocked") || result.includes("denied")) return { bg: "#fbe9e7", fg: "#b3261e" };
  return { bg: "#fbf2dc", fg: "#9a6a12" };
}

export function Person() {
  const { personId } = useParams({ from: "/person/$personId" });
  const [tab, setTab] = useState<Tab>("overview");
  const { data, isLoading, error } = useQuery({ queryKey: ["person", personId], queryFn: () => api.person(personId) });

  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const dir = data.directory;
  const dossier = data.dossier;
  const alerts: { tone: "bad" | "warn"; text: string }[] = [];
  if (data.breaches.length > 0) alerts.push({ tone: "bad", text: `Credentials found in a breach corpus — ${data.breaches[0]?.detail}` });
  if (dossier?.security?.cf.tor) alerts.push({ tone: "bad", text: "Correct password from a Tor exit node — MFA stopped it" });
  if (dossier?.security?.cf.impossibleTravel) alerts.push({ tone: "bad", text: "Impossible-travel sign-in detected" });
  if (dir?.mfa === "None") alerts.push({ tone: "warn", text: "MFA not enrolled" });
  if (dir?.status === "Suspended") alerts.push({ tone: "warn", text: "Account suspended" });

  return (
    <Page>
      <Link to="/clients/$clientId" params={{ clientId: data.clientId }} style={{ fontSize: 12, fontWeight: 600, color: "#8a8e88" }}>‹ {data.clientName}</Link>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <span style={{ width: 52, height: 52, borderRadius: "50%", background: "#e7e1d4", color: "#006747", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 18, flex: "none" }}>{data.initials}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>{data.name}</h1>
            {dir && <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: dir.status === "Active" ? "#e8f4ee" : "#fbe9e7", color: dir.status === "Active" ? "#1f8a5b" : "#b3261e" }}>{dir.status}</span>}
          </div>
          <div style={{ fontSize: 13.5, color: "#8a8e88", marginTop: 3 }}>{data.role} · {dir?.office} · {data.clientName} · <span style={{ fontFamily: "'Space Mono',monospace" }}>{data.memberKey}</span></div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {[["phone", "Call"], ["message-square", "Text"], ["shield-check", "Verify"], ["ticket", "New ticket"]].map(([icon, label]) => (
            <button key={label} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#006747", background: "#fff", border: "1px solid #e2e0d8", borderRadius: 7, padding: "7px 11px", cursor: "pointer" }}>
              <Icon name={icon!} size={14} color="#006747" />{label}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {alerts.map((a, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 9, background: a.tone === "bad" ? "#fbe9e7" : "#fbf2dc", border: `1px solid ${a.tone === "bad" ? "#f3d3ce" : "#f0ddca"}`, borderRadius: 6, padding: "9px 12px" }}>
              <Icon name={a.tone === "bad" ? "shield-alert" : "alert-triangle"} size={15} color={a.tone === "bad" ? "#b3261e" : "#9a6a12"} />
              <span style={{ fontSize: 12.5, color: a.tone === "bad" ? "#7a2a24" : "#5a4a22", fontWeight: 500 }}>{a.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #ececec" }}>
        {(["overview", "listings", "realtyid", "security", "billing"] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{ fontSize: 13, fontWeight: 600, border: 0, borderBottom: `2px solid ${tab === t ? "#006747" : "transparent"}`, background: "none", color: tab === t ? "#006747" : "#8a8e88", padding: "9px 12px", cursor: "pointer", textTransform: "capitalize" }}>
            {t === "realtyid" ? "RealtyID" : t}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview data={data} />}
      {tab === "listings" && <Listings data={data} />}
      {tab === "realtyid" && <RealtyId data={data} />}
      {tab === "security" && <Security data={data} />}
      {tab === "billing" && <Billing data={data} />}
    </Page>
  );
}


function Card({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Icon name={icon} size={15} color="#006747" />
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}
function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0", fontSize: 13, borderTop: "1px solid #f4f3ee" }}>
      <span style={{ color: "#8a8e88" }}>{k}</span>
      <span style={{ fontWeight: 500, textAlign: "right" }}>{v}</span>
    </div>
  );
}

function Overview({ data }: { data: PersonDetail }) {
  const dir = data.directory;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 16 }}>
      <Card title="Profile" icon="user-round">
        <Row k="Email" v={data.email} />
        <Row k="Phone" v={data.phone} />
        <Row k="Member since" v={dir?.joined} />
        <Row k="Last login" v={dir?.lastLogin} />
        <Row k="MFA" v={dir?.mfa} />
        <Row k="License" v={<span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12 }}>{dir?.license}</span>} />
        {dir?.notes ? <div style={{ marginTop: 10, fontSize: 12.5, color: "#4a504a", fontStyle: "italic" }}>{dir.notes}</div> : null}
      </Card>
      <Card title="Subscriptions" icon="sliders">
        {dir?.subscriptions.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {dir.subscriptions.map((s) => <span key={s} style={{ fontSize: 11.5, color: "#4a504a", background: "#f0efe9", borderRadius: 4, padding: "3px 8px" }}>{s}</span>)}
          </div>
        ) : <div style={{ fontSize: 12.5, color: "#8a8e88" }}>No add-ons.</div>}
        {dir?.tags.length ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
            {dir.tags.map((t) => <span key={t} style={{ fontSize: 11, color: "#006747", background: "#e8f1ec", borderRadius: 999, padding: "2px 9px", fontWeight: 600 }}>{t}</span>)}
          </div>
        ) : null}
      </Card>
      <Card title={`Support (${data.tickets.length})`} icon="ticket">
        {data.tickets.length === 0 && <div style={{ fontSize: 12.5, color: "#8a8e88" }}>No tickets.</div>}
        {data.tickets.map((t) => (
          <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: "1px solid #f4f3ee", fontSize: 12.5 }}>
            <span style={{ fontFamily: "'Space Mono',monospace", color: "#8a8e88", fontSize: 11 }}>{t.ref}</span>
            <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.subject}</span>
            <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: "#f0efe9", color: "#6b706a" }}>{t.status}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function Listings({ data }: { data: PersonDetail }) {
  const listings = data.directory?.listings ?? [];
  return (
    <Card title={`Listings (${listings.length})`} icon="home">
      {listings.length === 0 && <div style={{ fontSize: 12.5, color: "#8a8e88" }}>No listings.</div>}
      {listings.map((l, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid #f4f3ee" }}>
          <span style={{ width: 26, height: 26, borderRadius: 5, background: "#e8f4ee", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="home" size={13} color="#006747" /></span>
          <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{l[0]}</span>
          <span style={{ fontSize: 11.5, color: "#8a8e88" }}>{l[1]}</span>
          <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12.5 }}>{l[2]}</span>
        </div>
      ))}
    </Card>
  );
}

function RealtyId({ data }: { data: PersonDetail }) {
  const rid = data.dossier?.realtyId;
  if (!rid) return <Card title="RealtyID" icon="shield-check"><div style={{ fontSize: 12.5, color: "#8a8e88" }}>Identity not yet resolved for this member.</div></Card>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16 }}>
      <Card title="Identity" icon="shield-check">
        <Row k="RealtyID" v={<span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12 }}>{rid.rid}</span>} />
        {rid.ial != null && <Row k="Assurance (IAL)" v={`IAL${rid.ial}`} />}
        {rid.proofedAt && <Row k="Proofed" v={rid.proofedAt} />}
        <Row k="Searched" v={rid.searched.join(", ") || "—"} />
        <div style={{ marginTop: 10, fontSize: 12, color: "#4a504a", lineHeight: 1.5 }}>{rid.disciplineStatement}</div>
      </Card>
      <Card title="Licenses by jurisdiction" icon="scroll-text">
        {rid.licenses.length === 0 && <div style={{ fontSize: 12.5, color: "#8a8e88" }}>No licenses on file.</div>}
        {rid.licenses.map((l, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: "1px solid #f4f3ee" }}>
            <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 13, fontWeight: 700 }}>{l.state}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500 }}>{l.type} · {l.number}</div>
              <div style={{ fontSize: 11, color: "#8a8e88" }}>{l.expires ? `expires ${l.expires}` : "no expiry on file"}{l.asOf ? ` · as of ${l.asOf}` : ""}{l.primary ? " · primary" : ""}</div>
              <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2, color: l.eligible == null ? "#8a8e88" : l.eligible ? "#1f8a5b" : "#b3261e" }}>
                {l.eligible == null ? "Eligibility not available" : l.eligible ? "Eligible to practice" : "Not eligible to practice"}
              </div>
            </div>
            <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: String(l.status ?? "").toLowerCase() === "active" ? "#e8f4ee" : "#fbe9e7", color: String(l.status ?? "").toLowerCase() === "active" ? "#1f8a5b" : "#b3261e" }}>{l.status ?? "unknown"}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function Security({ data }: { data: PersonDetail }) {
  const sec = data.dossier?.security;
  const signins = data.dossier?.signins ?? [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {sec?.compromised && (
        <div style={{ background: "#fbe9e7", border: "1px solid #f3d3ce", borderRadius: 6, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Icon name="shield-alert" size={16} color="#b3261e" />
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "#b3261e" }}>Compromised credentials</span>
          </div>
          <div style={{ fontSize: 12.5, color: "#7a2a24" }}>{sec.compromised.source}</div>
          <div style={{ fontSize: 12, color: "#7a2a24", marginTop: 4 }}>Seen {sec.compromised.seen} · {sec.compromised.action}</div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr)", gap: 16 }}>
        <Card title="Sign-in map" icon="map">
          <SigninMap points={signins.map((s) => ({ lat: s.lat, lon: s.lon, bad: s.result.includes("blocked") || s.result.includes("denied") || s.result.includes("challenged"), label: s.location }))} />
          <div style={{ display: "flex", gap: 12, marginTop: 10, fontSize: 11, color: "#8a8e88" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#1f8a5b" }} />OK</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#b3261e" }} />Blocked / challenged</span>
            <span>· © OpenStreetMap</span>
          </div>
        </Card>
        <Card title="Cloudflare signals" icon="shield">
          {!sec ? (
            <div style={{ fontSize: 12.5, color: "#8a8e88" }}>No security signals on file — sign-in / Cloudflare checks have not run for this member.</div>
          ) : (
            <>
              <Row k="Threat level" v={<span style={{ color: sec.threat === "High" ? "#b3261e" : "#1f8a5b", fontWeight: 600 }}>{sec.threat}</span>} />
              <Row k="Min bot score" v={<span style={{ fontFamily: "'Space Mono',monospace" }}>{sec.cf.botScoreMin}</span>} />
              <Row k="Tor / anonymizer" v={sec.cf.tor ? "Yes" : "No"} />
              <Row k="Impossible travel" v={String(sec.cf.impossibleTravel ?? 0)} />
              <Row k="Countries" v={sec.cf.countries.join(", ")} />
              {sec.flags.length ? (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 5 }}>
                  {sec.flags.map((f, i) => (
                    <div key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start", fontSize: 12, color: "#b3261e" }}>
                      <Icon name="alert-triangle" size={12} color="#b3261e" style={{ marginTop: 2 }} />{f}
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </Card>
      </div>

      <Card title={`Sign-in log (${signins.length})`} icon="clock">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr 1fr .9fr .9fr", padding: "6px 0", fontSize: 10.5, fontWeight: 600, letterSpacing: ".5px", textTransform: "uppercase", color: "#a8aca6", borderBottom: "1px solid #f4f3ee" }}>
          <span>When</span><span>App</span><span>Location</span><span>Method</span><span style={{ textAlign: "right" }}>Result</span>
        </div>
        {signins.map((s, i) => {
          const rt = resultTone(s.result);
          return (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr 1fr .9fr .9fr", alignItems: "center", padding: "9px 0", fontSize: 12.5, borderTop: "1px solid #f4f3ee" }}>
              <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11.5 }}>{s.when}</span>
              <span>{s.app}</span>
              <span>{s.location}<span style={{ color: "#8a8e88", fontSize: 11 }}> · {s.asn}</span></span>
              <span style={{ fontSize: 11.5 }}>{s.method}</span>
              <span style={{ textAlign: "right" }}><span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: rt.bg, color: rt.fg }}>{s.result}</span></span>
            </div>
          );
        })}
        {signins.length === 0 && <div style={{ fontSize: 12.5, color: "#8a8e88", padding: "10px 0" }}>No sign-ins recorded.</div>}
      </Card>
    </div>
  );
}

function Billing({ data }: { data: PersonDetail }) {
  const b = data.dossier?.billing;
  if (!b) return <Card title="Billing" icon="receipt-text"><div style={{ fontSize: 12.5, color: "#8a8e88" }}>No billing on file.</div></Card>;
  const pastDue = b.status !== "Current";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.4fr)", gap: 16 }}>
      <Card title="Account" icon="receipt-text">
        <Row k="Status" v={<span style={{ color: pastDue ? "#b3261e" : "#1f8a5b", fontWeight: 600 }}>{b.status}</span>} />
        <Row k="Balance" v={<span style={{ fontFamily: "'Space Mono',monospace" }}>{b.balance}</span>} />
        {b.due ? <Row k="Due" v={<span style={{ color: "#b3261e" }}>{b.due}</span>} /> : null}
        <Row k="Plan" v={b.plan} />
      </Card>
      <Card title="Invoices" icon="scroll-text">
        {b.invoices.length === 0 && <div style={{ fontSize: 12.5, color: "#8a8e88" }}>No invoices.</div>}
        {b.invoices.map((inv, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid #f4f3ee", fontSize: 12.5 }}>
            <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 11.5, color: "#8a8e88" }}>{inv[0]}</span>
            <span style={{ flex: 1 }}>{inv[1]}</span>
            <span style={{ fontFamily: "'Space Mono',monospace" }}>{inv[2]}</span>
            <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: inv[3] === "Paid" ? "#e8f4ee" : "#fbe9e7", color: inv[3] === "Paid" ? "#1f8a5b" : "#b3261e" }}>{inv[3]}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}
